// =============================================================================
// features/assistant/edgeTab.ts — Lógica pura da aba lateral do copiloto no
// celular (F51-S37).
//
// Por que existe: o F30-S01 pôs um círculo de 56 px fixo no canto inferior
// direito, partindo de "o canto está livre". Não estava: no celular o compositor
// do chat põe o Enviar e o fim do campo de texto exatamente ali, e a SaveBar das
// configurações comerciais também. A aba sai da zona do rodapé (fica docada na
// borda direita, arrastável na vertical) e some onde ainda atrapalharia.
//
// Tudo aqui é função pura — sem DOM, sem React — para ser testável sem
// renderizar (convenção do repositório). Os hooks em `hooks/` só ligam estes
// cálculos aos eventos do navegador.
// =============================================================================

// ---------------------------------------------------------------------------
// Geometria
// ---------------------------------------------------------------------------

/** Altura da aba (parte visível e alvo de toque). */
export const TAB_HEIGHT_PX = 56;
/** Largura da parte VISÍVEL — estreita para não cobrir conteúdo na borda. */
export const TAB_VISIBLE_WIDTH_PX = 30;
/** Largura do alvo de toque: >= 44 px (WCAG 2.5.5), maior que a parte visível. */
export const TAB_HIT_WIDTH_PX = 44;

/** Topbar mobile (`h-14`). A aba nunca entra embaixo dela. */
export const TOPBAR_HEIGHT_PX = 56;
/** Respiro entre a topbar e a posição mais alta da aba. */
export const TOP_GAP_PX = 12;
/**
 * Faixa reservada no rodapé: compositores, barras de salvar e rodapés sticky
 * vivem aqui. A aba nunca desce para dentro dela.
 */
export const FOOTER_RESERVE_PX = 160;

/** Posição padrão: centro da aba a 45% da altura — zona livre de ações. */
export const DEFAULT_TAB_FRACTION = 0.45;

/** Movimento mínimo (px) para um toque virar arrasto. Abaixo disso, abre. */
export const DRAG_THRESHOLD_PX = 8;
/** Passo das setas ↑/↓ quando a aba está focada (alternativa ao arrasto). */
export const KEYBOARD_STEP_PX = 24;
/**
 * Redução do visualViewport que caracteriza teclado virtual aberto. Barras de
 * endereço que somem/aparecem mexem ~60–110 px; um teclado, 250 px ou mais.
 */
export const KEYBOARD_SHRINK_THRESHOLD_PX = 150;

/** Chave do localStorage: só a fração vertical, nada de dado pessoal. */
export const TAB_POSITION_STORAGE_KEY = 'elemento-assistant-tab-y';

export interface TabViewport {
  /** Altura do layout viewport em CSS px (`window.innerHeight`). */
  height: number;
  /** `env(safe-area-inset-top)` resolvido em px. */
  safeTop: number;
  /** `env(safe-area-inset-bottom)` resolvido em px. */
  safeBottom: number;
}

export interface TabBounds {
  /** Menor `top` (px) permitido. */
  min: number;
  /** Maior `top` (px) permitido. Nunca menor que `min`. */
  max: number;
}

function nonNegative(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

/**
 * Limites do `top` da aba. Em viewport baixo demais para caber entre a topbar e
 * a reserva do rodapé (paisagem com teclado), a topbar vence: a aba fica presa
 * no topo permitido — nesse cenário ela já está escondida pelo foco/teclado.
 */
export function computeTabBounds(viewport: TabViewport): TabBounds {
  const height = nonNegative(viewport.height);
  const min = TOPBAR_HEIGHT_PX + TOP_GAP_PX + nonNegative(viewport.safeTop);
  const max = height - FOOTER_RESERVE_PX - nonNegative(viewport.safeBottom) - TAB_HEIGHT_PX;
  return { min, max: Math.max(min, max) };
}

/** Prende um `top` nos limites. Valor não finito cai no limite superior. */
export function clampTabTop(top: number, bounds: TabBounds): number {
  if (!Number.isFinite(top)) return bounds.min;
  return Math.min(bounds.max, Math.max(bounds.min, top));
}

function isValidFraction(value: number): boolean {
  return Number.isFinite(value) && value >= 0 && value <= 1;
}

/** Fração (centro da aba / altura do viewport) → `top` em px, já com clamp. */
export function tabTopFromFraction(fraction: number, viewport: TabViewport): number {
  const safeFraction = isValidFraction(fraction) ? fraction : DEFAULT_TAB_FRACTION;
  const top = safeFraction * nonNegative(viewport.height) - TAB_HEIGHT_PX / 2;
  return clampTabTop(top, computeTabBounds(viewport));
}

/**
 * `top` em px → fração do viewport. A fração (e não o px) é o que se guarda:
 * sobrevive a rotação e a barras de endereço que mudam a altura.
 */
export function fractionFromTabTop(top: number, viewportHeight: number): number {
  if (!Number.isFinite(top) || !Number.isFinite(viewportHeight) || viewportHeight <= 0) {
    return DEFAULT_TAB_FRACTION;
  }
  const fraction = (top + TAB_HEIGHT_PX / 2) / viewportHeight;
  return Math.min(1, Math.max(0, fraction));
}

// ---------------------------------------------------------------------------
// Persistência (localStorage) — nunca confiar no que está salvo
// ---------------------------------------------------------------------------

/** Só dígitos com ponto opcional: rejeita "", " ", "1e-1", "0x1", "-0.2". */
const STORED_FRACTION_PATTERN = /^\d{1,3}(\.\d{1,8})?$/;

/** Lê a fração salva. Qualquer coisa fora do formato/faixa → padrão. */
export function parseStoredTabFraction(raw: string | null | undefined): number {
  if (typeof raw !== 'string') return DEFAULT_TAB_FRACTION;
  const trimmed = raw.trim();
  if (!STORED_FRACTION_PATTERN.test(trimmed)) return DEFAULT_TAB_FRACTION;
  const value = Number(trimmed);
  return isValidFraction(value) ? value : DEFAULT_TAB_FRACTION;
}

/** Serializa a fração (4 casas bastam: 0,01% de 1000 px = 0,1 px). */
export function serializeTabFraction(fraction: number): string {
  const safe = isValidFraction(fraction) ? fraction : DEFAULT_TAB_FRACTION;
  return safe.toFixed(4);
}

// ---------------------------------------------------------------------------
// Gestos e teclado
// ---------------------------------------------------------------------------

/** Distingue arrasto de toque: só vira arrasto a partir do limiar. */
export function isDragGesture(deltaY: number): boolean {
  return Number.isFinite(deltaY) && Math.abs(deltaY) >= DRAG_THRESHOLD_PX;
}

/** Um passo de teclado (↑ sobe, ↓ desce), respeitando os limites. */
export function stepTabTop(top: number, direction: 'up' | 'down', bounds: TabBounds): number {
  const base = clampTabTop(top, bounds);
  const delta = direction === 'up' ? -KEYBOARD_STEP_PX : KEYBOARD_STEP_PX;
  return clampTabTop(base + delta, bounds);
}

// ---------------------------------------------------------------------------
// Onde a aba some
// ---------------------------------------------------------------------------

export interface HiddenRoute {
  /** Padrão de rota do react-router; `:param` casa um segmento qualquer. */
  pattern: string;
  /** Por que a aba atrapalharia aqui. */
  reason: string;
}

/**
 * Rotas com compositor ou barra/rodapé ancorado embaixo. Cada padrão também
 * cobre as sub-rotas. Levantado em 19/10/2026 a partir do `App.tsx` + grep de
 * `fixed`/`sticky bottom-0`/`safe-area-inset-bottom` em `apps/web/src`.
 * Tela nova com barra inferior ancorada entra AQUI.
 */
export const ASSISTANT_TAB_HIDDEN_ROUTES: readonly HiddenRoute[] = [
  {
    pattern: '/conversas',
    reason: 'Inbox (ConversationsLayout): MessageComposer ancorado com Enviar à direita.',
  },
  {
    pattern: '/meu-whatsapp/inbox',
    reason: 'Caixa pessoal do vendedor (F43-S13): o mesmo ConversationsLayout e compositor.',
  },
  {
    pattern: '/configuracoes/comercial',
    reason: 'SaveBar fixa no rodapé (fixed bottom-0) com Salvar/Descartar à direita.',
  },
  {
    pattern: '/imports/leads/new',
    reason: 'Assistente de importação: rodapé sticky com Voltar/Avançar.',
  },
  {
    pattern: '/campanhas/:campaignId/operar',
    reason: 'Operação de campanha: OperationFooter sticky com as ações do contato.',
  },
];

function pathSegments(path: string): string[] {
  // react-router casa sem diferenciar maiúsculas; query/hash não fazem parte.
  const [base = ''] = path.toLowerCase().split(/[?#]/, 1);
  return base.split('/').filter((segment) => segment.length > 0);
}

/** Casa o padrão por segmentos; segmentos extras = sub-rota (também casa). */
export function matchesRoutePattern(pathname: string, pattern: string): boolean {
  const path = pathSegments(pathname);
  const expected = pathSegments(pattern);
  if (expected.length === 0 || path.length < expected.length) return false;
  return expected.every((segment, index) => segment.startsWith(':') || segment === path[index]);
}

export function isAssistantTabHiddenRoute(pathname: string): boolean {
  return ASSISTANT_TAB_HIDDEN_ROUTES.some((route) => matchesRoutePattern(pathname, route.pattern));
}

/** O que importa de um elemento focado para saber se ele abre teclado. */
export interface EditableDescriptor {
  tagName: string;
  type?: string | null;
  isContentEditable?: boolean;
  role?: string | null;
}

/** Tipos de `<input>` que NÃO são de digitação (não abrem teclado de texto). */
const NON_TEXT_INPUT_TYPES: ReadonlySet<string> = new Set([
  'checkbox',
  'radio',
  'button',
  'submit',
  'reset',
  'image',
  'range',
  'file',
  'color',
  'hidden',
]);

const TEXTBOX_ROLES: ReadonlySet<string> = new Set(['textbox', 'searchbox']);

/** O elemento focado é um campo editável (o usuário está escrevendo)? */
export function isEditableElement(element: EditableDescriptor): boolean {
  if (element.isContentEditable === true) return true;
  const role = (element.role ?? '').trim().toLowerCase();
  if (TEXTBOX_ROLES.has(role)) return true;

  const tag = element.tagName.toLowerCase();
  if (tag === 'textarea' || tag === 'select') return true;
  if (tag !== 'input') return false;

  // `<input>` sem type (ou com type vazio/desconhecido) é text para o navegador.
  const type = (element.type ?? '').trim().toLowerCase();
  return !NON_TEXT_INPUT_TYPES.has(type);
}

export interface KeyboardViewport {
  /** `window.innerHeight` (layout viewport). */
  layoutHeight: number;
  /** `visualViewport.height`. */
  visualHeight: number;
  /** `visualViewport.scale` — zoom de pinça encolhe o visual sem teclado. */
  scale: number;
}

/** O teclado virtual está aberto? (visual viewport bem menor que o layout) */
export function isVirtualKeyboardOpen(viewport: KeyboardViewport): boolean {
  const { layoutHeight, visualHeight } = viewport;
  if (!Number.isFinite(layoutHeight) || !Number.isFinite(visualHeight)) return false;
  const scale = Number.isFinite(viewport.scale) && viewport.scale > 0 ? viewport.scale : 1;
  // Com zoom, visualHeight está em px "ampliados": reconverter antes de comparar.
  return layoutHeight - visualHeight * scale > KEYBOARD_SHRINK_THRESHOLD_PX;
}

export interface AssistantTabVisibilityInput {
  pathname: string;
  editableFocused: boolean;
  keyboardOpen?: boolean;
}

/** A aba some na rota com rodapé ancorado e enquanto o usuário digita. */
export function shouldHideAssistantTab(input: AssistantTabVisibilityInput): boolean {
  return (
    input.editableFocused ||
    input.keyboardOpen === true ||
    isAssistantTabHiddenRoute(input.pathname)
  );
}
